import styled from "@emotion/styled";
import PropTypes from "prop-types";
import React from "react";

import BaseLink from "~/components/links/BaseLink";
import ButtonLink from "~/components/links/ButtonLink";

import IcoIcon from "~/components/IcoIcon";
import Image from "~/components/Image";

import transition from "~/utilities/transition";

import theme from "~/theme";

const Columns = styled.div`
    display: grid;
    grid-template-columns: ${({ columns }) => `repeat(${columns}, 1fr)`};
    grid-gap: ${({ theme }) => theme.spacing.smallest};

    @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
        grid-gap: ${({ theme }) => theme.layout.gutter};
    }
`;

const Card = styled.div`
    display: flex;
    flex-direction: column;
    background-color: ${({ theme }) => theme.palette.white};
`;

const LogoWrapper = styled.div`
    background-color: ${({ backgroundColor }) => backgroundColor};
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 90px;
    overflow: hidden;
`;

const LogoImage = styled(Image)`
    text-align: center;

    img {
        max-height: 90px;
        width: auto;
        max-width: 280px;
        object-fit: contain;
    }
`;

const ContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 30px;
    width: 100%;
`;

const Title = styled.div`
    font-weight: 900;
    font-size: 2.2rem;
    margin-bottom: 15px;
`;

const SubTitle = styled.div`
    font-size: 1.8rem;
    margin-bottom: ${({ theme }) => theme.layout.gutter};
`;

const ButtonWrapper = styled.div`
    margin-top: ${({ theme }) => theme.layout.gutter};
`;

const AdditionalLinks = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-width: 250px;
    margin-top: ${({ theme }) => theme.layout.gutter};
`;

const AdditionalLink = styled(BaseLink)`
    ${transition({}, "color")};
    font-size: 1.6rem;
    font-weight: 900;
    display: flex;
    align-items: center;

    &:hover,
    &:focus {
        color: ${({ theme }) => theme.palette.lightBlue};
    }
`;

const Icon = styled(IcoIcon)`
    margin-left: 5px;
`;

const SubNavCards = ({ cards = [], tagLabel = null, tagRef = null }) => (
    <Columns columns={cards.length}>
        {cards.map(
            (
                {
                    logo,
                    logoBackgroundColor,
                    title,
                    subTitle,
                    productsImage,
                    buttonLabel,
                    buttonUrl,
                    additionalLinks,
                },
                index,
            ) => (
                <Card key={`${index}-${title}`}>
                    {logo ? (
                        <LogoWrapper
                            backgroundColor={
                                logoBackgroundColor ?? theme.palette.darkBlue
                            }
                        >
                            <LogoImage
                                title={logo.title}
                                altText={logo.altText}
                                sourceUrl={logo.sourceUrl}
                                objectFit="contain"
                            />
                        </LogoWrapper>
                    ) : null}
                    <ContentWrapper>
                        {title ? <Title>{title}</Title> : null}

                        {subTitle ? <SubTitle>{subTitle}</SubTitle> : null}

                        {productsImage ? (
                            <Image
                                title={productsImage.title}
                                altText={productsImage.altText}
                                sourceUrl={productsImage.sourceUrl}
                                objectFit="contain"
                            />
                        ) : null}

                        {buttonLabel && buttonUrl ? (
                            <ButtonWrapper>
                                <ButtonLink
                                    to={buttonUrl}
                                    backgroundColor="yellow"
                                    tagRef={tagRef}
                                    tagLabel={tagLabel}
                                >
                                    {buttonLabel}
                                </ButtonLink>
                            </ButtonWrapper>
                        ) : null}

                        {additionalLinks.length ? (
                            <AdditionalLinks>
                                {additionalLinks.map(
                                    ({ linkLabel, linkUrl }, index) => (
                                        <AdditionalLink
                                            to={linkUrl}
                                            key={`${index}-${linkLabel}`}
                                            tagLabel={tagLabel}
                                            tagRef={tagRef}
                                        >
                                            {linkLabel}
                                            <Icon
                                                size={16}
                                                icon="arrow-right"
                                            />
                                        </AdditionalLink>
                                    ),
                                )}
                            </AdditionalLinks>
                        ) : null}
                    </ContentWrapper>
                </Card>
            ),
        )}
    </Columns>
);

SubNavCards.propTypes = {
    cards: PropTypes.arrayOf(
        PropTypes.shape({
            logo: PropTypes.shape({
                sourceUrl: PropTypes.string,
                altText: PropTypes.string,
                title: PropTypes.string.isRequired,
            }),
            logoBackgroundColor: PropTypes.string,
            title: PropTypes.string,
            subTitle: PropTypes.string,
            productsImage: PropTypes.shape({
                sourceUrl: PropTypes.string,
                altText: PropTypes.string,
                title: PropTypes.string.isRequired,
            }),
            buttonLabel: PropTypes.string,
            buttonUrl: PropTypes.string,
            additionalLinks: PropTypes.arrayOf(
                PropTypes.shape({
                    linkLabel: PropTypes.string.isRequired,
                    linkUrl: PropTypes.string.isRequired,
                }),
            ),
        }),
    ).isRequired,
    tagLabel: PropTypes.string,
    tagRef: PropTypes.object,
};

export default SubNavCards;
